<template>
  <main class="container">
    <div
      class="card p-0 alert alert-secondary"
      :class="{ 'outline-green': !canvasActive && showOutline }"
    >
      <div class="card-header">
        <div class="row py-2">
          <div class="col-4">
            <RouterLink
              :to="{ name: 'ImageSelect' }"
              class="btn btn-primary fw-bold"
              ><span class="bi-arrow-left"></span> Return to Image Selection
            </RouterLink>
          </div>
          <div class="col my-auto">
            <h1 class="fs-3 fw-bold m-auto">{{ activeImage.name }}</h1>
          </div>
        </div>
      </div>
      <div class="card-body">
        <div class="row">
          <div class="col-4">
            <div class="alert alert-info p-2">
              <div
                class="d-flex align-items-center"
                style="cursor: pointer"
                @click="showKeyboardNav = !showKeyboardNav"
              >
                <span class="bi bi-info-circle-fill me-2"></span>
                <span class="fw-bold">Keyboard Navigation</span>
                <span
                  class="ms-auto bi"
                  :class="showKeyboardNav ? 'bi-chevron-up' : 'bi-chevron-down'"
                ></span>
              </div>
              <transition name="fade">
                <div v-if="showKeyboardNav" class="mt-3">
                  <ul class="text-start list-unstyled mb-0">
                    <li>
                      <span class="badge bg-secondary me-2">C</span>
                      Select Circle
                    </li>
                    <li>
                      <span class="badge bg-secondary me-2">R</span>
                      Select Rectangle
                    </li>
                    <li>
                      <span class="badge bg-secondary me-2">T</span>
                      Select Text
                    </li>
                    <li>
                      <span class="badge bg-secondary me-2">B</span>
                      Select Bracket
                    </li>
                    <li>
                      <span class="fw-bold">Select Draw (Mouse Required)</span>
                    </li>
                    <li>
                      <span class="badge bg-secondary me-2">Enter</span>
                      Place mark on image
                    </li>
                    <li>
                      <span class="badge bg-secondary me-2">`</span>
                      Toggle between Tools and Canvas for editing marks
                    </li>
                    <li>
                      <span class="badge bg-secondary me-2">Esc</span>
                      Reset selection
                    </li>
                    <li>
                      <span class="badge bg-secondary me-2">Delete</span>
                      <span class="badge bg-secondary me-2">Backspace</span>
                      Delete selected mark
                    </li>
                    <li>
                      <span class="badge bg-secondary me-2">
                        <i class="bi-arrow-up"></i>
                      </span>
                      <span class="badge bg-secondary me-2">
                        <i class="bi-arrow-down"></i>
                      </span>
                      <span class="badge bg-secondary me-2">
                        <i class="bi-arrow-left"></i>
                      </span>
                      <span class="badge bg-secondary me-2">
                        <i class="bi-arrow-right"></i>
                      </span>
                      Move selection
                    </li>
                    <li>
                      <span class="badge bg-secondary me-2">[</span>
                      <span class="badge bg-secondary me-2">]</span>
                      Size decrease and increase
                    </li>
                    <li>
                      <span class="badge bg-secondary me-2">PgUp</span>
                      <span class="badge bg-secondary me-2">PgDn</span>
                      Rotation
                    </li>
                  </ul>
                </div>
              </transition>
            </div>
            <div class="card shadow-sm p-3">
              <!-- Beginning of control panel for markup -->
              <div class="card mb-3">
                <div class="card-header fw-bold text-start">
                  1. Select a Mark
                </div>
                <div class="card-body">
                  <div class="w-100">
                    <button
                      type="button"
                      class="btn w-100 mb-3 fw-bold d-flex align-items-center justify-content-between text-dark"
                      :class="[
                        markType === selectedMarkType && isPlacingMark
                          ? 'btn-primary text-white'
                          : 'btn-outline-secondary'
                      ]"
                      v-for="markType in markTypes"
                      :key="markType"
                      @click="changeMarkType(markType)"
                    >
                      <span
                        class="text-capitalize"
                        v-if="isPlacingMark && markType === selectedMarkType"
                      >
                        {{ markType }} Selected
                      </span>
                      <!-- Logic for icon -->
                      <span class="text-capitalize" v-else
                        >Select {{ markType }} </span
                      ><span :class="markTypeIcon(markType)"></span>
                      <span class="fw-normal" v-if="markType === 'bracket'"
                        >[</span
                      >
                    </button>
                  </div>
                </div>
              </div>
              <div class="card mb-3 text-start fw-bold">
                <div class="card-header text-capitalize fw-bold">
                  2. Configure Mark
                </div>
                <div class="card-body">
                  <div class="dropdown mb-3">
                    <button
                      class="btn btn-primary dropdown-toggle btn-lg fw-bold w-100 d-flex align-items-center justify-content-between"
                      type="button"
                      id="dropdownMenuButton"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                      ref="colorDropdown"
                    >
                      <span
                        class="color-swatch my-auto"
                        :style="`background-color: ${strokeColor}`"
                      ></span>
                      Mark Color
                    </button>
                    <ul
                      class="dropdown-menu"
                      aria-labelledby="dropdownMenuButton"
                    >
                      <li>
                        <a
                          class="dropdown-item"
                          @click="strokeColor = 'red'"
                          tabindex="0"
                          @keydown.enter.prevent="strokeColor = 'red'"
                          @keydown.space.prevent="strokeColor = 'red'"
                        >
                          <span
                            class="color-swatch my-auto"
                            style="background-color: red"
                          ></span>
                          Red
                        </a>
                      </li>
                      <li>
                        <a
                          class="dropdown-item"
                          @click="strokeColor = 'black'"
                          tabindex="0"
                          @keydown.enter.prevent="strokeColor = 'black'"
                          @keydown.space.prevent="strokeColor = 'black'"
                        >
                          <span
                            class="color-swatch my-auto"
                            style="background-color: black"
                          ></span>
                          Black
                        </a>
                      </li>
                      <li>
                        <a
                          class="dropdown-item"
                          @click="strokeColor = 'white'"
                          tabindex="0"
                          @keydown.enter.prevent="strokeColor = 'white'"
                          @keydown.space.prevent="strokeColor = 'white'"
                        >
                          <span
                            class="color-swatch"
                            style="background-color: white"
                          ></span>
                          White
                        </a>
                      </li>
                    </ul>
                  </div>
                  <div v-if="selectedMarkType === 'rectangle'">
                    <div>
                      <label>
                        <input type="checkbox" v-model="lineIsDashed" />
                        Dashed Line
                      </label>
                    </div>
                    <hr />
                    <p>3. Click on the image to place a rectangle or</p>
                    <button
                      class="btn btn-primary btn-lg mb-3 fw-bold"
                      @click="applyMarkToImage"
                      type="button"
                    >
                      Apply Rectangle to Image<span
                        class="ms-2 bi bi-arrow-right-square"
                      ></span>
                    </button>
                    <p>
                      4. Click and drag the rectangle to adjust the position and
                      size
                    </p>
                  </div>
                  <div v-if="selectedMarkType === 'circle'">
                    <div>
                      <label>
                        <input type="checkbox" v-model="lineIsDashed" />
                        Dashed Line
                      </label>
                    </div>
                    <hr />
                    <p>3. Click on the image to place a circle or</p>
                    <button
                      class="btn btn-primary btn-lg mb-3 fw-bold"
                      @click="applyMarkToImage"
                      type="button"
                    >
                      Apply Circle to Image<span
                        class="ms-2 bi bi-arrow-right-square"
                      ></span>
                    </button>
                    <p>
                      4. Click and drag the circle to adjust the position and
                      size
                    </p>
                  </div>
                  <div v-if="selectedMarkType === 'text'">
                    <p>
                      3. Type the text you would like to place on the image:
                    </p>
                    <input
                      class="form-control mb-3"
                      type="text"
                      v-model="labelText"
                    />
                    <p>4. Click on the image to place the text</p>
                    <button
                      class="btn btn-primary btn-lg mb-3 fw-bold"
                      @click="applyMarkToImage"
                      type="button"
                    >
                      Apply Text to Image<span
                        class="ms-2 bi bi-arrow-right-square"
                      ></span>
                    </button>
                    <p>
                      5. Click on the text to select and adjust the position and
                      size
                    </p>
                  </div>
                  <div v-if="selectedMarkType === 'bracket'">
                    <p>3. Click on the image to place a bracket</p>
                    <button
                      class="btn btn-primary btn-lg mb-3 fw-bold"
                      @click="applyMarkToImage"
                      type="button"
                    >
                      Apply Bracket to Image<span
                        class="ms-2 bi bi-arrow-right-square"
                      ></span>
                    </button>
                    <p>
                      4. Click on bracket to select and adjust the position and
                      size
                    </p>
                  </div>
                  <!-- Drawn Line Mark -->
                  <div v-if="selectedMarkType === 'draw'">
                    <div>
                      <label>
                        <input type="checkbox" v-model="lineIsDashed" />
                        Dashed Line
                      </label>
                    </div>
                    <hr />
                    <p>3. Free draw on image.</p>
                  </div>
                  <!--  -->
                </div>
              </div>
              <button
                class="btn btn-primary btn-lg fw-bold mb-3 d-flex align-items-center justify-content-between"
                @click="deleteMark"
              >
                Clear Selected Mark <span class="bi-file-x"></span>
              </button>
              <button
                class="btn btn-primary btn-lg fw-bold mb-3 d-flex align-items-center justify-content-between"
                @click="clearMarks"
              >
                Clear All Marks <span class="bi-folder-x"></span>
              </button>
              <button
                class="btn btn-primary btn-lg fw-bold d-flex align-items-center justify-content-between"
                @click="downloadImage"
              >
                Download Image <span class="bi-download"></span>
              </button>
            </div>
          </div>
          <div class="col-8">
            <div class="alert alert-primary">
              <div class="row text-start">
                <div class="col" v-if="cellsToIdentify.length > 0">
                  <p class="my-auto fw-bold">Cells To Identify:</p>
                  <ul>
                    <li v-for="cell in cellsToIdentify" :key="cell">
                      {{ cell }}
                    </li>
                  </ul>
                </div>
                <div class="col">
                  <p class="my-auto fw-bold">Features To Identify:</p>
                  <ul>
                    <li v-for="cell in featuresToIdentify" :key="cell">
                      {{ cell }}
                    </li>
                  </ul>
                </div>
                <div class="col" v-if="additionalInfo.length > 0">
                  <p class="my-auto fw-bold">Additional Info:</p>
                  <ul>
                    <li v-for="cell in additionalInfo" :key="cell">
                      {{ cell }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div
              class="card shadow-sm p-3"
              ref="card"
              :class="{ 'outline-green': canvasActive && showOutline }"
            >
              <div ref="maincontainer" class="rounded">
                <v-stage
                  ref="stage"
                  :config="configStage"
                  @mouseup="handleStageMouseUp"
                  @mousemove="handleStageMouseMove"
                  @mousedown="handleStageMouseDown"
                >
                  <v-layer ref="layer">
                    <v-image
                      :config="configImage"
                      class="rounded"
                      ref="slide"
                    />
                    <v-rect
                      v-for="rectangle in rectangles"
                      :key="rectangle.name"
                      :config="rectangle"
                      :dash="dashSpacing"
                      @transformend="handleTransformEnd"
                      :tabindex="0"
                      @focus="
                        selectedShapeName = rectangle.name;
                        updateTransformer();
                      "
                    />
                    <v-circle
                      v-for="circle in circles"
                      :key="circle.name"
                      :config="circle"
                      draggable="true"
                      :dash="dashSpacing"
                      @transformend="handleTransformEnd"
                      :tabindex="0"
                      @focus="
                        selectedShapeName = circle.name;
                        updateTransformer();
                      "
                    />
                    <v-text
                      v-for="text in texts"
                      :key="text.name"
                      :config="text"
                      draggable="true"
                      @transformend="handleTransformEnd"
                      :tabindex="0"
                      @focus="
                        selectedShapeName = text.name;
                        updateTransformer();
                      "
                    />
                    <v-shape
                      v-for="bracket in brackets"
                      :key="bracket.name"
                      :config="bracket"
                      draggable="true"
                      @transformend="handleTransformEnd"
                      :tabindex="0"
                      @focus="
                        selectedShapeName = bracket.name;
                        updateTransformer();
                      "
                    />
                    <!-- Rendered Drawn Line -->
                    <v-line
                      v-for="line in drawn"
                      :key="line.name"
                      :config="line"
                      draggable="true"
                      :dash="dashSpacing"
                      @transformend="handleTransformEnd"
                    />
                    <!--  -->
                    <v-transformer :ignoreStroke="true" ref="transformer" />
                  </v-layer>
                </v-stage>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import { v4 as uuidv4 } from "uuid";
import html2canvas from "html2canvas";
import { mapGetters, mapActions } from "vuex";
import keyboardAccessibility from "@/helpers/keyboardAccessibility";

export default {
  mixins: [keyboardAccessibility],
  name: "MarkupView",
  data() {
    return {
      stageSize: {
        width: 1024,
        height: 1024
      },
      markTypes: ["circle", "rectangle", "text", "bracket", "draw"],
      isPlacingMark: false,
      isDrawing: false,
      currentLineIndex: 0,
      lineIsDashed: false,
      dashSpacing: [10, 5],

      // All marks are stored in the following arrays
      rectangles: [],
      circles: [],
      texts: [],
      brackets: [],
      drawn: [],

      // The following variables are used to keep track of the currently selected mark
      selectedShapeName: "",
      selectedMarkType: "",

      labelText: "Label", // The text to be placed on the image

      clientWidth: null, // The width of the image container
      strokeColor: "black", // The color of the marks - used in construction
      strokeWidth: 5, // The width of the marks - used in construction
      showKeyboardNav: false,
      canvasActive: false, // false = tools, true = canvas
      showOutline: false, // controls outline fade
      outlineTimeout: null // for fade timer
    };
  },
  mounted() {
    this.clientWidth = this.$refs.maincontainer.clientWidth;
    window.addEventListener("resize", this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.handleResize);
  },
  computed: {
    ...mapGetters({
      capturedImage: "getCapturedImage",
      activeImage: "getActiveImage",
      cellsToIdentify: "getCellsToIdentify",
      featuresToIdentify: "getFeaturesToIdentify",
      additionalInfo: "getAdditionalInfo",
      ONID: "getONID"
    }),

    configStage() {
      // If the image is loaded, scale width to clientWidth and height to auto (preserve aspect ratio)
      if (
        this.capturedImage &&
        this.capturedImage.naturalWidth &&
        this.capturedImage.naturalHeight
      ) {
        const width = this.clientWidth;
        const aspectRatio =
          this.capturedImage.naturalWidth / this.capturedImage.naturalHeight;
        const height = width / aspectRatio;
        return {
          width,
          height
        };
      }
      // fallback
      return {
        width: this.clientWidth,
        height: this.clientWidth
      };
    },
    configImage() {
      const image = new window.Image();
      image.src = this.imgSrc(this.activeImage.src);
      image.onload = () => {
        this.setCapturedImage(image);
      };
      // If the image is loaded, scale width to clientWidth and height to auto (preserve aspect ratio)
      if (
        this.capturedImage &&
        this.capturedImage.naturalWidth &&
        this.capturedImage.naturalHeight
      ) {
        const width = this.clientWidth;
        const aspectRatio =
          this.capturedImage.naturalWidth / this.capturedImage.naturalHeight;
        const height = width / aspectRatio;
        return {
          image: this.capturedImage,
          width,
          height
        };
      }
      // fallback
      return {
        image: this.capturedImage
      };
    }
  },
  methods: {
    ...mapActions({
      setCapturedImage: "setCapturedImage"
    }),
    imgSrc(src) {
      return require(`@/assets/${src}`);
    },
    handleResize() {
      // Update the clientWidth when the window is resized
      this.clientWidth = this.$refs.maincontainer.clientWidth;
    },
    deleteMark() {
      // Find this.selectedShapeName in the appropriate array and remove it
      if (this.selectedShapeName.slice(0, 4) === "rect") {
        this.rectangles = this.rectangles.filter(
          (rect) => rect.name !== this.selectedShapeName
        );
      } else if (this.selectedShapeName.slice(0, 4) === "circ") {
        this.circles = this.circles.filter(
          (circle) => circle.name !== this.selectedShapeName
        );
      } else if (this.selectedShapeName.slice(0, 4) === "text") {
        this.texts = this.texts.filter(
          (text) => text.name !== this.selectedShapeName
        );
      } else if (this.selectedShapeName.slice(0, 4) === "brac") {
        this.brackets = this.brackets.filter(
          (bracket) => bracket.name !== this.selectedShapeName
        );
      } else if (this.selectedShapeName.slice(0, 4) === "draw") {
        this.drawn = this.drawn.filter(
          (line) => line.name !== this.selectedShapeName
        );
      }
      this.selectedShapeName = "";
      this.updateTransformer();
    },
    clearMarks() {
      // Clear all marks from the image
      this.rectangles = [];
      this.circles = [];
      this.texts = [];
      this.brackets = [];
      this.drawn = [];
      this.selectedShapeName = "";
      this.currentLineIndex = 0; // Reset line index for drawing
      this.updateTransformer();
    },
    changeMarkType(markType) {
      // Change the mark type
      this.selectedMarkType = markType;
      this.isPlacingMark = true;
      // If the mark type is text, clear the label text
      if (markType === "text") {
        this.labelText = "";
      }
      // Focus the mark color dropdown after keyboard selection
      this.$nextTick(() => {
        if (
          markType === "circle" ||
          markType === "rectangle" ||
          markType === "text" ||
          markType === "bracket"
        ) {
          const dropdown = this.$refs.colorDropdown;
          if (dropdown && typeof dropdown.focus === "function") {
            dropdown.focus();
          }
        }
      });
    },
    markTypeIcon(markType) {
      // Return the icon for the mark type button
      if (markType === "circle") {
        return "bi-circle";
      } else if (markType === "rectangle") {
        return "bi-square";
      } else if (markType === "text") {
        return "bi-type";
      } else if (markType === "bracket") {
        return;
      } else if (markType === "draw") {
        return "bi-pencil";
      }
    },
    handleStageMouseDown(e) {
      //
      // Mouse down has two logical outcomes
      // placing a new object
      // scaling/rotating an object
      //
      // We use isPlacingMark to deterine
      //
      // this is set by selecting a tool button
      //
      if (this.isPlacingMark) {
        this.addMark(e);
      } else {
        this.handleSelect(e);
      }
    },
    addMark(event) {
      const stage = event.target.getStage();
      const pointerPosition = stage.getPointerPosition();
      if (!this.isPlacingMark) return;
      if (this.selectedMarkType === "rectangle") {
        this.rectangles.push({
          name: "rect" + uuidv4(),
          x: pointerPosition.x - 50,
          y: pointerPosition.y - 50,
          rotation: 0,
          width: 100,
          height: 100,
          scaleX: 1,
          scaleY: 1,
          fill: "transparent",
          stroke: this.strokeColor,
          strokeScaleEnabled: false,
          strokeWidth: this.strokeWidth,
          dashEnabled: this.lineIsDashed,
          draggable: true
        });
        this.isPlacingMark = false;
      }
      if (this.selectedMarkType === "circle") {
        this.circles.push({
          name: "circ" + uuidv4(),
          x: pointerPosition.x,
          y: pointerPosition.y,
          radius: 50,
          fill: "transparent",
          stroke: this.strokeColor,
          strokeScaleEnabled: false,
          strokeWidth: this.strokeWidth,
          dashEnabled: this.lineIsDashed,
          draggable: true,
          scaleX: 1, // ensure scale properties exist
          scaleY: 1,
          rotation: 0
        });
        this.isPlacingMark = false;
      }
      if (this.selectedMarkType === "text") {
        if (this.labelText === "") {
          alert("Please enter text to add");
        } else {
          this.texts.push({
            name: "text" + uuidv4(),
            text: this.labelText,
            fontSize: 40,
            fill: this.strokeColor,
            x: pointerPosition.x,
            y: pointerPosition.y - 10,
            align: "center",
            verticalAlign: "middle",
            scaleX: 1,
            scaleY: 1,
            rotation: 0
          });
          this.isPlacingMark = false;
        }
      }

      if (this.selectedMarkType === "bracket") {
        this.brackets.push({
          name: "bracket" + uuidv4(),
          x: pointerPosition.x,
          y: pointerPosition.y,
          width: 15,
          height: 100,
          sceneFunc: (context, shape) => {
            context.beginPath();
            context.moveTo(15, 0);
            context.lineTo(0, 0);
            context.lineTo(0, 100);
            context.lineTo(15, 100);
            context.fillStrokeShape(shape);
          },
          stroke: this.strokeColor,
          strokeScaleEnabled: false,
          strokeWidth: this.strokeWidth,
          draggable: true,
          scaleX: 1,
          scaleY: 1,
          rotation: 0
        });
        this.isPlacingMark = false;
      }

      if (this.selectedMarkType === "draw") {
        const lineName = "draw" + uuidv4();
        this.drawn.push({
          name: lineName,
          x: pointerPosition.x,
          y: pointerPosition.y,
          points: [0, 0],
          stroke: this.strokeColor,
          tension: 1,
          closed: false,
          strokeScaleEnabled: false,
          strokeWidth: this.strokeWidth,
          dashEnabled: this.lineIsDashed,
          draggable: true
        });
        this.isPlacingMark = false;
        this.isDrawing = true;
      }
    },

    addPoints(x, y) {
      const line = this.drawn[this.currentLineIndex];
      if (!line) return; // Guard: do nothing if line doesn't exist
      line.points.push(x - line.x);
      line.points.push(y - line.y);
      //
      // we are altering the points array within the array
      // of draw lines, so we don't trigger reactivity
      // normally.
      //
      // To force a rerender we need to change the name
      // -- THANKS GITHUB COPILOT
      line.name = "draw" + uuidv4();
    },

    handleTransformEnd(e) {
      // shape is transformed, let us save new attrs back to the node
      // find element in our state
      if (typeof e.target.name !== "string") {
        return;
      }

      const name = e.target.name();
      // Rectangle
      if (name.slice(0, 4) === "rect") {
        const rect = this.rectangles.find((r) => r.name === name);
        if (rect) {
          rect.x = e.target.x();
          rect.y = e.target.y();
          rect.rotation = e.target.rotation();
          rect.scaleX = e.target.scaleX();
          rect.scaleY = e.target.scaleY();
        }
      }
      // Circle
      if (name.slice(0, 4) === "circ") {
        const circle = this.circles.find((c) => c.name === name);
        if (circle) {
          circle.x = e.target.x();
          circle.y = e.target.y();
          circle.radius = e.target.radius();
          circle.rotation = e.target.rotation && e.target.rotation();
          circle.scaleX = e.target.scaleX && e.target.scaleX();
          circle.scaleY = e.target.scaleY && e.target.scaleY();
        }
      }
      // Text
      if (name.slice(0, 4) === "text") {
        const text = this.texts.find((t) => t.name === name);
        if (text) {
          text.x = e.target.x();
          text.y = e.target.y();
          text.rotation = e.target.rotation();
          text.scaleX = e.target.scaleX();
          text.scaleY = e.target.scaleY();
        }
      }
      // Bracket
      if (name.slice(0, 7) === "bracket") {
        const bracket = this.brackets.find((b) => b.name === name);
        if (bracket) {
          bracket.x = e.target.x();
          bracket.y = e.target.y();
          bracket.rotation = e.target.rotation();
          bracket.scaleX = e.target.scaleX && e.target.scaleX();
          bracket.scaleY = e.target.scaleY && e.target.scaleY();
        }
      }
      // Drawn line (optional: if you want to allow transform)
      if (name.slice(0, 4) === "draw") {
        const line = this.drawn.find((d) => d.name === name);
        if (line) {
          line.x = e.target.x();
          line.y = e.target.y();
          line.rotation = e.target.rotation && e.target.rotation();
          line.scaleX = e.target.scaleX && e.target.scaleX();
          line.scaleY = e.target.scaleY && e.target.scaleY();
        }
      }
    },
    handleSelect(e) {
      // clicked on stage - clear selection
      if (e.target === e.target.getStage()) {
        this.selectedShapeName = "";
        this.updateTransformer();
        return;
      }

      // clicked on transformer - do nothing
      const clickedOnTransformer =
        e.target.getParent().className === "Transformer";
      if (clickedOnTransformer) {
        return;
      }

      // find clicked shape by its name
      const name = e.target.name();
      let shape;
      if (name.startsWith("rect")) {
        shape = this.rectangles.find((r) => r.name === name);
      } else if (name.startsWith("circ")) {
        shape = this.circles.find((c) => c.name === name);
      } else if (name.startsWith("text")) {
        shape = this.texts.find((t) => t.name === name);
      } else if (name.startsWith("bracket")) {
        shape = this.brackets.find((b) => b.name === name);
      } else if (name.startsWith("draw")) {
        shape = this.drawn.find((d) => d.name === name);
      }

      if (shape) {
        this.selectedShapeName = name;
      } else {
        this.selectedShapeName = "";
      }
      this.updateTransformer();
    },
    handleStageMouseMove(e) {
      if (this.isDrawing) {
        const pos = e.target.getStage().getPointerPosition();
        this.addPoints(pos.x, pos.y);
      }
    },
    handleStageMouseUp(e) {
      if (this.isDrawing) {
        const pos = e.target.getStage().getPointerPosition();
        this.addPoints(pos.x, pos.y);
        this.isDrawing = false;
        this.currentLineIndex++;
      }
      this.updateTransformer();
    },
    updateTransformer() {
      // here we need to manually attach or detach Transformer node
      const transformerNode = this.$refs.transformer.getNode();

      const stage = transformerNode.getStage();
      const { selectedShapeName } = this;

      const selectedNode = stage.findOne("." + selectedShapeName);
      // do nothing if selected node is already attached
      if (selectedNode === transformerNode.node()) {
        return;
      }

      if (selectedNode) {
        // attach to another node
        transformerNode.nodes([selectedNode]);
      } else {
        // remove transformer
        transformerNode.nodes([]);
      }
    },
    async downloadImage() {
      // deselect the currently selected shape
      this.selectedShapeName = "";
      // for the updateTransformer method to complete
      await new Promise((resolve) => requestAnimationFrame(resolve));

      // capture the image and download
      const stage = this.$refs.maincontainer;
      const canvas = await html2canvas(stage);
      const link = document.createElement("a");
      link.download = `${this.ONID}-${this.activeImage.name}.png`;
      link.href = canvas.toDataURL();
      link.click();
    },
    setActiveRegion(isCanvas) {
      this.canvasActive = isCanvas;
      this.showOutline = true;
      if (this.outlineTimeout) clearTimeout(this.outlineTimeout);
      this.outlineTimeout = setTimeout(() => {
        this.showOutline = false;
      }, 2000); // 2 seconds
    },
    applyMarkToImage() {
      // Simulate placing the mark at the center of the canvas (same as Enter key)
      if (!this.isPlacingMark) return;
      const stage = this.$refs.stage.getNode();
      const pos = { x: this.clientWidth / 2, y: this.clientWidth / 2 };
      const evt = {
        target: stage,
        getStage: () => stage,
        // Simulate Konva pointer position
        ...{
          target: {
            getStage: () => stage
          }
        }
      };
      // Patch getPointerPosition for addMark
      stage.getPointerPosition = () => pos;
      this.addMark(evt);
    }
  }
};
</script>

<style scoped>
.color-swatch {
  display: inline-block;
  width: 60px;
  height: 20px;
  margin-right: 10px;
  border: 1px solid #ccc;
  vertical-align: left;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.outline-green {
  /* Active region outline */
  box-shadow: 0 0 0 4px #39ff14 !important;
  transition: box-shadow 0.5s;
  z-index: 10;
  position: relative;
}
</style>
